import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HorizontalTabsList,
  Icons,
  LoadingIndicator,
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@commenda/kakashi';

import { IndirectTaxFilingsHelper } from '../helpers';
import type {
  BreakdownLiabilityReportTableRow,
  ContributionLiabilityReportTableRow,
  IIndirectTaxFiling,
} from '../types';

import {
  INDIRECT_TAX_LIABILITY_REPORT_TAB_LABELS,
  IndirectTaxTaxLiabilityReportTab,
} from './indirectTaxTaxLiabilityReportTab';
import { useIndirectTaxTaxLiabilityReportModal } from './useIndirectTaxTaxLiabilityReportModal';

interface IndirectTaxTaxLiabilityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  filings: IIndirectTaxFiling[];
  periodLabel: string;
}

const IndirectTaxTaxLiabilityReportModal = ({
  isOpen,
  onClose,
  corporationId,
  filings,
  periodLabel,
}: IndirectTaxTaxLiabilityReportModalProps) => {
  const {
    activeLiabilityReportTab,
    setActiveLiabilityReportTab,
    liabilityReportTabFromControlValue,
    loadState,
    loadError,
    filingsWithBreakdown,
    filingsWithContributions,
    breakdownTableRows,
    contributionTableRows,
    aggregateSummaryForActiveLiabilityReportTab,
    isBreakdownTabScopeEmpty,
    isContributionTabScopeEmpty,
    handleExportCSV,
    breakdownColumnGroups,
    contributionColumnGroups,
    contributionTabExpandableRowConfig,
  } = useIndirectTaxTaxLiabilityReportModal({
    isOpen,
    corporationId,
    filings,
    periodLabel,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="bg-white min-w-[800px] max-w-[80vw] flex flex-col h-[800px] max-h-[70vh] p-0 overflow-hidden"
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>Tax liability report</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3xl p-3xl overflow-y-auto flex-1">
          {loadState === 'loading' && (
            <div className="flex items-center justify-center py-10xl">
              <LoadingIndicator />
            </div>
          )}

          {loadState === 'error' && loadError && (
            <p className="txt-small-regular text-text-error">{loadError}</p>
          )}

          {loadState === 'success' && (
            <>
              <div className="flex items-center gap-3xl flex-wrap">
                <div className="flex flex-col gap-xs">
                  <span className="txt-extra-small-regular text-text-tertiary">
                    Jurisdictions
                  </span>
                  <span className="txt-large-bold text-text-primary">
                    {
                      aggregateSummaryForActiveLiabilityReportTab.totalJurisdictions
                    }
                  </span>
                </div>
                <div className="flex flex-col gap-xs">
                  <span className="txt-extra-small-regular text-text-tertiary">
                    Tax due
                  </span>
                  {aggregateSummaryForActiveLiabilityReportTab.taxDueByCurrency
                    .length === 0 ? (
                    <span className="txt-large-bold text-text-primary">
                      N/A
                    </span>
                  ) : (
                    aggregateSummaryForActiveLiabilityReportTab.taxDueByCurrency.map(
                      ({ currency, totalTaxDue }) => (
                        <span
                          key={currency}
                          className="txt-large-bold text-text-primary"
                        >
                          {totalTaxDue === null
                            ? 'N/A (currency unavailable)'
                            : `${currency} ${IndirectTaxFilingsHelper.formatCurrency(totalTaxDue)}`}
                        </span>
                      )
                    )
                  )}
                </div>
                <div className="flex-1" />
                <Button variant="secondary" size="sm" onClick={handleExportCSV}>
                  <Icons.Download01 className="size-4" />
                  <span>Export CSV</span>
                </Button>
              </div>

              <Tabs
                value={activeLiabilityReportTab}
                onValueChange={(tabControlValue) =>
                  setActiveLiabilityReportTab(
                    liabilityReportTabFromControlValue(tabControlValue)
                  )
                }
              >
                <HorizontalTabsList
                  variant="underline"
                  className="border-b-0 gap-lg"
                >
                  <TabsTrigger
                    value={
                      IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
                    }
                  >
                    {
                      INDIRECT_TAX_LIABILITY_REPORT_TAB_LABELS[
                        IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada
                      ]
                    }
                    {filingsWithBreakdown.length > 0 ? (
                      <span className="ml-1 txt-extra-small-regular text-text-tertiary">
                        ({filingsWithBreakdown.length})
                      </span>
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger
                    value={
                      IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom
                    }
                  >
                    {
                      INDIRECT_TAX_LIABILITY_REPORT_TAB_LABELS[
                        IndirectTaxTaxLiabilityReportTab
                          .EuropeanUnionAndUnitedKingdom
                      ]
                    }
                    {filingsWithContributions.length > 0 ? (
                      <span className="ml-1 txt-extra-small-regular text-text-tertiary">
                        ({filingsWithContributions.length})
                      </span>
                    ) : null}
                  </TabsTrigger>
                </HorizontalTabsList>

                <TabsContent
                  value={IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada}
                  className="mt-3xl"
                >
                  <DataTable<BreakdownLiabilityReportTableRow, unknown>
                    key={`${IndirectTaxTaxLiabilityReportTab.UnitedStatesAndCanada}-${breakdownTableRows.length}`}
                    columnGroups={breakdownColumnGroups}
                    data={breakdownTableRows}
                    searchConfig={{
                      isEnabled: true,
                      columnsToEnableSearchFor: ['jurisdiction', 'formTitle'],
                      placeholder: 'Search jurisdiction or return',
                    }}
                    emptyStateConfig={{
                      title: 'No filings in this scope',
                      description: isBreakdownTabScopeEmpty
                        ? 'No United States or Canadian filings with breakdown data found.'
                        : 'No rows match your search.',
                    }}
                  />
                </TabsContent>

                <TabsContent
                  value={
                    IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom
                  }
                  className="mt-3xl"
                >
                  <DataTable<ContributionLiabilityReportTableRow, unknown>
                    key={`${IndirectTaxTaxLiabilityReportTab.EuropeanUnionAndUnitedKingdom}-${contributionTableRows.length}`}
                    columnGroups={contributionColumnGroups}
                    data={contributionTableRows}
                    expandableRowConfig={contributionTabExpandableRowConfig}
                    searchConfig={{
                      isEnabled: true,
                      columnsToEnableSearchFor: ['jurisdiction', 'formTitle'],
                      placeholder: 'Search jurisdiction or return',
                    }}
                    emptyStateConfig={{
                      title: 'No filings in this scope',
                      description: isContributionTabScopeEmpty
                        ? 'No European Union or United Kingdom filings with contribution data found.'
                        : 'No rows match your search.',
                    }}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxTaxLiabilityReportModal };
